import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    SearchIcon,
    MenuIcon,
    XIcon,
    HomeIcon,
    FolderIcon,
    MailIcon,
    CodeIcon,
    UserIcon,
    Palette,
    FileIcon,
    Briefcase,
    ArrowRight
} from 'lucide-react';
import { useSiteConfig } from '@/hooks/use-site-settings';

// Utility function
function cn(...inputs: any[]) {
    return inputs.filter(Boolean).join(' ');
}

// Search result types
interface SearchResult {
    id: string;
    type: 'project' | 'service' | 'page';
    title: string;
    description: string;
    url: string;
}

// Static pages for search
const staticPages: SearchResult[] = [
    { id: 'home', type: 'page', title: 'Início', description: 'Página inicial do portfólio', url: '/' },
    { id: 'projects', type: 'page', title: 'Projetos', description: 'Ver todos os projetos de design e desenvolvimento', url: '/projetos' },
    { id: 'services', type: 'page', title: 'Serviços', description: 'Serviços de design, branding e desenvolvimento', url: '/servicos' },
    { id: 'about', type: 'page', title: 'Sobre', description: 'Conheça mais sobre Cássio', url: '/sobre' },
    { id: 'contact', type: 'page', title: 'Contato', description: 'Entre em contato para orçamentos', url: '/contato' },
    { id: 'dev', type: 'page', title: 'Desenvolvimento', description: 'Projetos de desenvolvimento web', url: '/desenvolvimento' },
];

// Static services for search
const staticServices: SearchResult[] = [
    { id: 'branding', type: 'service', title: 'Branding & Identidade Visual', description: 'Criação e redesign de marcas memoráveis', url: '/servicos' },
    { id: 'uiux', type: 'service', title: 'UI/UX Design', description: 'Design de interfaces digitais intuitivas', url: '/servicos' },
    { id: 'motion', type: 'service', title: 'Motion Graphics & Vídeo', description: 'Animações e motion design para mídias digitais', url: '/servicos' },
    { id: 'editorial', type: 'service', title: 'Design Editorial', description: 'Layout e diagramação para publicações', url: '/servicos' },
    { id: 'illustration', type: 'service', title: 'Ilustração & Arte Digital', description: 'Ilustrações customizadas', url: '/servicos' },
    { id: 'art-direction', type: 'service', title: 'Direção de Arte', description: 'Campanhas publicitárias impactantes', url: '/servicos' },
];

// Sheet Components
function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
    return <SheetPrimitive.Root {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
    return <SheetPrimitive.Portal {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
    return (
        <SheetPrimitive.Overlay
            className={cn(
                'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className
            )}
            {...props}
        />
    );
}

function SheetContent({
    className,
    children,
    side = 'right',
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
    side?: 'top' | 'right' | 'bottom' | 'left';
}) {
    return (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                className={cn(
                    'fixed z-50 bg-black/95 backdrop-blur-xl shadow-2xl transition ease-in-out',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:duration-300 data-[state=open]:duration-500',
                    side === 'left' &&
                    'inset-y-0 left-0 h-full w-3/4 border-r border-blue-500/20 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
                    className
                )}
                {...props}
            >
                {children}
                <SheetPrimitive.Close className="absolute right-4 top-4 rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    <XIcon className="h-5 w-5" />
                    <span className="sr-only">Fechar</span>
                </SheetPrimitive.Close>
            </SheetPrimitive.Content>
        </SheetPortal>
    );
}

// Badge Component
interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: 'default' | 'blue' | 'orange';
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
    ({ className, variant = 'default', ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                    variant === 'default' && 'bg-white/10 text-white',
                    variant === 'blue' && 'bg-blue-500/20 text-blue-300',
                    variant === 'orange' && 'bg-indigo-500/20 text-indigo-300',
                    className
                )}
                {...props}
            />
        );
    }
);
Badge.displayName = 'Badge';

// Nav Item interface
interface NavItem {
    label: string;
    href: string;
    icon?: React.ReactNode;
}

// Tab Component for Desktop Nav
const Tab = ({
    children,
    setPosition,
    href,
    isActive,
}: {
    children: React.ReactNode;
    setPosition: any;
    href: string;
    isActive: boolean;
}) => {
    const ref = React.useRef<HTMLLIElement>(null);
    return (
        <li
            ref={ref}
            onMouseEnter={() => {
                if (!ref.current) return;
                const { width } = ref.current.getBoundingClientRect();
                setPosition({
                    width,
                    opacity: 1,
                    left: ref.current.offsetLeft,
                });
            }}
            className={cn(
                "relative z-10 block cursor-pointer px-4 py-2 text-sm font-medium transition-colors",
                isActive ? "text-white" : "text-white/70 hover:text-white"
            )}
        >
            <Link to={href}>{children}</Link>
        </li>
    );
};

// Cursor Component for Desktop Nav
const Cursor = ({ position }: { position: any }) => {
    return (
        <motion.li
            animate={position}
            className="absolute z-0 h-9 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30"
        />
    );
};

// Get icon for result type
const getResultIcon = (type: string) => {
    switch (type) {
        case 'page':
            return <FileIcon className="h-4 w-4" />;
        case 'service':
            return <Briefcase className="h-4 w-4" />;
        case 'project':
            return <FolderIcon className="h-4 w-4" />;
        default:
            return <FileIcon className="h-4 w-4" />;
    }
};

// Main Header Component
export default function PremiumHeader() {
    const [open, setOpen] = React.useState(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [position, setPosition] = React.useState({
        left: 0,
        width: 0,
        opacity: 0,
    });
    const location = useLocation();
    const navigate = useNavigate();
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const { data: siteConfig } = useSiteConfig();

    const navItems: NavItem[] = [
        { label: 'Início', href: '/', icon: <HomeIcon className="h-4 w-4" /> },
        { label: 'Projetos', href: '/projetos', icon: <FolderIcon className="h-4 w-4" /> },
        { label: 'Dev', href: '/desenvolvimento', icon: <CodeIcon className="h-4 w-4" /> },
        { label: 'Serviços', href: '/servicos', icon: <Palette className="h-4 w-4" /> },
        { label: 'Sobre', href: '/sobre', icon: <UserIcon className="h-4 w-4" /> },
        { label: 'Contato', href: '/contato', icon: <MailIcon className="h-4 w-4" /> },
    ];

    const isActive = (path: string) => location.pathname === path;

    // Search function
    const performSearch = React.useCallback((query: string) => {
        setSearchQuery(query);
        setSelectedIndex(0);

        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        const lowerQuery = query.toLowerCase();

        const pageResults = staticPages.filter(
            page =>
                page.title.toLowerCase().includes(lowerQuery) ||
                page.description.toLowerCase().includes(lowerQuery)
        );

        const serviceResults = staticServices.filter(
            service =>
                service.title.toLowerCase().includes(lowerQuery) ||
                service.description.toLowerCase().includes(lowerQuery)
        );

        setSearchResults([...pageResults, ...serviceResults].slice(0, 8));
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && searchResults.length > 0) {
            e.preventDefault();
            const selected = searchResults[selectedIndex];
            if (selected) {
                navigate(selected.url);
                setSearchOpen(false);
                setSearchQuery('');
                setSearchResults([]);
            }
        } else if (e.key === 'Escape') {
            setSearchOpen(false);
            setSearchQuery('');
            setSearchResults([]);
        }
    }, [searchResults, selectedIndex, navigate]);

    // Keyboard shortcut (Cmd/Ctrl + K)
    React.useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    // Focus input when search opens
    React.useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchOpen]);

    // Close menu on route change
    React.useEffect(() => {
        setOpen(false);
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    }, [location.pathname]);

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-xl">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-[94px] items-center justify-between">
                        {/* Logo */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setOpen(true)}
                                className="lg:hidden rounded-lg p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <MenuIcon className="h-5 w-5" />
                            </button>
                            <Link to="/" className="flex items-center group">
                                {siteConfig?.logo_url ? (
                                    <img
                                        src={siteConfig.logo_url}
                                        alt="Logo"
                                        className="h-10 min-w-[40px] object-contain"
                                    />
                                ) : (
                                    <div className="h-10 min-w-[40px] rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center font-bold text-white text-sm group-hover:scale-110 transition-transform">
                                        {siteConfig?.site_name?.[0] || 'C'}
                                    </div>
                                )}
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:block">
                            <ul
                                className="relative flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1"
                                onMouseLeave={() => setPosition((pv) => ({ ...pv, opacity: 0 }))}
                            >
                                {navItems.map((item) => (
                                    <Tab
                                        key={item.label}
                                        setPosition={setPosition}
                                        href={item.href}
                                        isActive={isActive(item.href)}
                                    >
                                        {item.label}
                                    </Tab>
                                ))}
                                <Cursor position={position} />
                            </ul>
                        </nav>

                        {/* Right Actions */}
                        <div className="flex items-center gap-2">
                            {/* Search */}
                            <button
                                onClick={() => setSearchOpen(!searchOpen)}
                                className="hidden sm:flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <SearchIcon className="h-4 w-4" />
                                <span className="hidden md:inline">Buscar</span>
                                <kbd className="hidden md:inline-flex h-5 items-center gap-1 rounded border border-white/20 bg-white/10 px-1.5 text-xs">
                                    ⌘K
                                </kbd>
                            </button>

                            {/* Mobile Search */}
                            <button
                                onClick={() => setSearchOpen(!searchOpen)}
                                className="sm:hidden rounded-lg p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <SearchIcon className="h-5 w-5" />
                            </button>

                            {/* CTA Button */}
                            <Link
                                to="/contato"
                                className="hidden sm:flex items-center gap-2 rounded-lg liquid-glass-btn-solid px-4 py-2 text-sm font-medium text-white"
                            >
                                Falar comigo
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search Bar Expanded */}
                <AnimatePresence>
                    {searchOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="border-t border-white/10 bg-black/95 backdrop-blur-xl"
                        >
                            <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6 lg:px-8">
                                <div className="relative">
                                    <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/50" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => performSearch(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Buscar projetos, serviços, páginas..."
                                        className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-12 pr-4 text-white placeholder:text-white/50 focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => {
                                                setSearchQuery('');
                                                setSearchResults([]);
                                            }}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                                        >
                                            <XIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Search Results */}
                                {searchResults.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-3 rounded-xl border border-white/10 bg-white/5 overflow-hidden"
                                    >
                                        {searchResults.map((result, index) => (
                                            <button
                                                key={result.id}
                                                onClick={() => {
                                                    navigate(result.url);
                                                    setSearchOpen(false);
                                                    setSearchQuery('');
                                                    setSearchResults([]);
                                                }}
                                                className={cn(
                                                    "w-full flex items-center gap-4 px-4 py-3 text-left transition-colors",
                                                    index === selectedIndex
                                                        ? "bg-blue-500/20 text-white"
                                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                                )}
                                            >
                                                <div className={cn(
                                                    "w-8 h-8 rounded-lg flex items-center justify-center",
                                                    result.type === 'page' && "bg-blue-500/20 text-blue-400",
                                                    result.type === 'service' && "bg-indigo-500/20 text-indigo-400",
                                                    result.type === 'project' && "bg-blue-500/20 text-blue-400"
                                                )}>
                                                    {getResultIcon(result.type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium truncate">{result.title}</p>
                                                    <p className="text-sm text-white/50 truncate">{result.description}</p>
                                                </div>
                                                <ArrowRight className="h-4 w-4 text-white/30" />
                                            </button>
                                        ))}
                                    </motion.div>
                                )}

                                {/* No results */}
                                {searchQuery && searchResults.length === 0 && (
                                    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                                        <p className="text-white/50">Nenhum resultado encontrado para "{searchQuery}"</p>
                                    </div>
                                )}

                                {/* Quick links when empty */}
                                {!searchQuery && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="text-xs text-white/50">Sugestões:</span>
                                        {['Projetos', 'Branding', 'UI/UX', 'Contato'].map((suggestion) => (
                                            <button
                                                key={suggestion}
                                                onClick={() => performSearch(suggestion)}
                                                className="px-3 py-1 text-xs rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            {/* Mobile Menu */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="left" className="w-80">
                    <div className="flex flex-col h-full pt-16">
                        {/* User Info */}
                        <div className="flex items-center gap-3 px-6 pb-6 border-b border-white/10">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center font-bold text-white text-lg">
                                C
                            </div>
                            <div>
                                <p className="font-semibold text-white">Cássio</p>
                                <p className="text-sm text-white/60">Design & Arte</p>
                            </div>
                        </div>

                        {/* Navigation */}
                        <nav className="flex-1 overflow-y-auto py-6">
                            <ul className="space-y-1 px-3">
                                {navItems.map((item) => (
                                    <li key={item.label}>
                                        <Link
                                            to={item.href}
                                            onClick={() => setOpen(false)}
                                            className={cn(
                                                "flex items-center gap-3 rounded-lg px-3 py-3 transition-colors",
                                                isActive(item.href)
                                                    ? "bg-white/10 text-white"
                                                    : "text-white/70 hover:text-white hover:bg-white/10"
                                            )}
                                        >
                                            {item.icon}
                                            <span className="font-medium">{item.label}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {/* Footer */}
                        <div className="border-t border-white/10 p-6">
                            <Link
                                to="/contato"
                                onClick={() => setOpen(false)}
                                className="flex items-center justify-center gap-2 w-full rounded-lg liquid-glass-btn-solid px-4 py-3 text-sm font-medium text-white"
                            >
                                Falar comigo
                            </Link>
                            <div className="flex items-center gap-2 mt-4 justify-center">
                                <Badge variant="blue">Portfólio</Badge>
                                <Badge variant="blue">2026</Badge>
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
